import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { CodeBlock } from "../../CodeOutputBlocks";

const Forms_CS = ({ subtopicId, goalName, courseName, subtopic }) => {
  const { markSubtopicComplete, loadProgressSummary, completedContent } =
    useAuth();

  const [isSubtopicCompleted, setIsSubtopicCompleted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [mcqAnswers, setMcqAnswers] = useState({});

  // Check if subtopic is already completed
  useEffect(() => {
    if (completedContent.includes(subtopicId)) {
      setIsSubtopicCompleted(true);
    }
  }, [completedContent, subtopicId]);

  const handleAnswer = (question, option) => {
    setMcqAnswers((prev) => ({ ...prev, [question]: option }));
  };

  const handleContinue = async () => {
    if (isLoading || isSubtopicCompleted) return;

    try {
      setIsLoading(true);
      const result = await markSubtopicComplete(
        subtopicId,
        goalName,
        courseName
      );

      if (result.success) {
        await loadProgressSummary();
        setIsSubtopicCompleted(true);
        console.log("✅ Cheat sheet marked as completed");
      } else {
        console.error(
          "❌ Failed to mark cheat sheet complete:",
          result.message
        );
        alert("Failed to mark as complete. Please try again.");
      }
    } catch (error) {
      console.error("❌ Failed to mark cheat sheet complete:", error);
      alert("Failed to mark as complete. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="intro-container">
      <h1>Forms | Cheat Sheet</h1>

      {/* 1. HTML Forms */}
      <section>
        <h2>1. HTML Forms</h2>
        <p>HTML Forms are used to collect data from the user.</p>
        <p>Forms can be of different kinds:</p>
        <ul>
          <li>Login / Sign in Form</li>
          <li>Registration Form</li>
          <li>Contact Us Form, etc.</li>
        </ul>

        <h3>1.1 HTML Form Element</h3>
        <p>
          The HTML <code>form</code> element can be used to create HTML forms.
          It is a container that can contain different types of Input elements
          like Text Fields, Checkboxes, etc.
        </p>
        <CodeBlock language="html" code={`<form></form>`} />

        <div className="Note-container">
          <div className="icon-note">
            <h6>
              <i class="bi bi-journal-text"></i>Note
            </h6>
          </div>
          <p>
            Whenever we click a button or press <b>Enter</b> key while editing
            any input field in the form, the <b>submit</b> event will be
            triggered.
          </p>
        </div>
      </section>

      {/* 2. Event Object Methods */}
      <section>
        <h2>2. Event Object Methods</h2>
        <h3>2.1 preventDefault()</h3>
        <p>
          The <code>preventDefault()</code> method prevents the occurrence of
          default action.
        </p>
        <p>
          Here in the form, it prevents the default behaviour of the{" "}
          <code>submit</code> event.
        </p>
        <CodeBlock
          language="javascript"
          code={`let myFormEl = document.getElementById("myForm");
myFormEl.addEventListener("submit", function(event) {
  event.preventDefault();
});`}
        />
      </section>

      {/* 3. Event Types */}
      <section>
        <h2>3. Event Types</h2>
        <p>There are different types of events.</p>
        <ul>
          <li>Keyboard Events</li>
          <li>Mouse Events</li>
          <li>Touch Events</li>
          <li>Form Events, etc.</li>
        </ul>

        <h3>3.1 Form Events</h3>
        <p>A Form Event is an event that can occur within a form.</p>
        <p>Some of the form events are:</p>
        <ul>
          <li>blur</li>
          <li>focus</li>
          <li>change, etc.</li>
        </ul>

        <h3>3.1.1 Blur Event</h3>
        <p>
          The <code>blur</code> event happens when an HTML element has lost
          focus.
        </p>
        <CodeBlock
          language="javascript"
          code={`let nameEl = document.getElementById("name");
nameEl.addEventListener("blur", function(event) {
  console.log("blur event triggered");
});`}
        />
      </section>

      {/* Continue Button */}
      <div className="view-continue">
        <button
          className={`btn-continue ${isSubtopicCompleted ? "completed" : ""}`}
          onClick={handleContinue}
          disabled={isSubtopicCompleted || isLoading}
        >
          {isLoading
            ? "Marking..."
            : isSubtopicCompleted
            ? "✓ Completed"
            : "Continue"}
        </button>
      </div>
    </div>
  );
};

export default Forms_CS;
